import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CommentsAndPostsModeration from '../moderation-v2/comments-and-posts';
import SecondaryNavigation from '../common/navigation/secondary-navigation';
import {
  cancelCommentsRealtimeCounter,
  startCommentsRealtimeCounter
} from '../moderation-v2/comments-and-posts/state/actions';
import ModerationUsers from '../moderation-v2/users';
import BlacklistedIps from '../moderation-v2/advanced/ips';
import ArticlesDashboard from 'app/scripts/components/moderation-v2/articles/components/ArticlesDashboard';
import classnames from 'classnames';
import { UserContext } from 'app/scripts/components/moderation-v2/contexts/UserContext';
import { TenantConfigLoader } from 'app/scripts/tenant-config/TenantConfigLoader';
import {
  IS_MODERATION_BY_ARTICLES_ENABLED,
  moderationModeratorConfigMap,
  RESTRICTED_GROUPS_ENABLED
} from 'app/scripts/tenant-config/tenantConfigConstants';

const mapStateToProps = state => {
  return { tenantConfig: state.tenantConfig };
};

const mapDispatchToProps = dispatch => ({
  pollRealtimeCommentsCount: () => {
    dispatch(startCommentsRealtimeCounter());
  },
  cancelRealtimeCommentsCount: () => {
    dispatch(cancelCommentsRealtimeCounter());
  }
});

const RestrictedWordsNext = React.lazy(function () {
  return import(
    /* webpackChunkName: "restricted-words-next" */ '../moderation-v2/restricted-words-next/components/RestrictedWords'
  );
});

const RestrictedWords = React.lazy(function () {
  return import(/* webpackChunkName: "restricted-words" */ '../moderation-v2/words/index');
});

class Moderation extends React.Component {
  static displayName = 'Moderation';

  static propTypes = {
    page: PropTypes.string.isRequired,
    spot: PropTypes.object,
    spotId: PropTypes.string.isRequired,
    userId: PropTypes.string,
    ip: PropTypes.string,
    className: PropTypes.string,
    isMultiSelect: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    itemsList: PropTypes.array,
    pageInfo: PropTypes.object,
    expirationTime: PropTypes.string,
    top: PropTypes.number,
    conversationId: PropTypes.string,
    conversation: PropTypes.object,
    isAdmin: PropTypes.bool,
    isSuperAdmin: PropTypes.bool,
    isModerationViewer: PropTypes.bool,
    isSearchUserExpanded: PropTypes.bool,
    usernameOrEmail: PropTypes.string,
    currentUser: PropTypes.object,
    activeItems: PropTypes.number,
    isModerationMode: PropTypes.bool,
    counts: PropTypes.object,
    searchTerm: PropTypes.string,
    pollRealtimeCommentsCount: PropTypes.func,
    cancelRealtimeCommentsCount: PropTypes.func,
    lastFetch: PropTypes.number,
    tenantConfig: PropTypes.object
  };

  static contextTypes = {
    executeAction: PropTypes.func.isRequired
  };

  render() {
    var component = null;
    var {
      page,
      className,
      spotId,
      spot,
      isSuperAdmin,
      isModerationViewer,
      currentRoute,
      currentUser,
      tenantConfig
    } = this.props;

    const isModerationByArticlesEnabled = tenantConfig.values[IS_MODERATION_BY_ARTICLES_ENABLED];
    const isRestrictedGroupsEnabled = tenantConfig.values[RESTRICTED_GROUPS_ENABLED];
    const isConfigLoaded =
      isModerationByArticlesEnabled !== undefined && isRestrictedGroupsEnabled !== undefined;

    let isMultipleModeratorsMode = spot && spot.isMultipleModeratorsMode;
    let inpageSkin = spot && spot.inpageSkin;
    let spotLanguage = spot && spot.mainLanguage;
    const isViewOnlyMode = isModerationViewer;
    let contentType = 'COMMENTS';

    switch (page) {
      case 'moderation-posts':
      case 'moderation-posts-folders':
        contentType = 'POSTS';
      case 'moderation':
      case 'moderation-comments':
      case 'moderation-comments-folders':
        component = (
          <CommentsAndPostsModeration
            spotId={spotId}
            spotLanguage={spotLanguage}
            currentRoute={currentRoute}
            currentUser={currentUser}
            inpageSkin={inpageSkin}
            contentType={contentType}
            isMultipleModeratorsMode={isMultipleModeratorsMode}
            isModerationViewer={isModerationViewer}
          />
        );
        break;
      case 'moderation-restricted-words':
        component = spot && spotId && isConfigLoaded && (
          <React.Suspense fallback={<div>Loading...</div>}>
            {isRestrictedGroupsEnabled ? (
              <UserContext.Provider value={{ isViewer: isViewOnlyMode }}>
                <RestrictedWordsNext />
              </UserContext.Provider>
            ) : (
              <RestrictedWords spotId={spotId} isViewOnlyMode={isViewOnlyMode} />
            )}
          </React.Suspense>
        );
        break;
      case 'moderation-users-blacklisted':
      case 'moderation-users-whitelisted':
        component = spot && (
          <ModerationUsers
            spot={spot}
            page={page}
            spotId={spotId}
            isViewOnlyMode={isViewOnlyMode}
          />
        );
        break;
      case 'moderation-advanced':
        component = (
          <div>
            <BlacklistedIps spotId={spotId} isViewOnlyMode={isViewOnlyMode} />
          </div>
        );
        break;
      case 'moderation-articles':
        component = <ArticlesDashboard />;
        break;
    }

    var secondaryMenuItems = [
      {
        label: 'COMMENTS',
        routeName: 'moderation-comments-folders',
        navParams: {
          spot_id: spotId,
          folder: 'pending',
          tag: 'all',
          offset: 0
        },
        notification: {
          onMount: this.props.pollRealtimeCommentsCount,
          onUnmount: this.props.cancelRealtimeCommentsCount
        }
      }
    ];

    if (isModerationByArticlesEnabled || isSuperAdmin) {
      secondaryMenuItems.push({
        label: 'ARTICLES',
        routeName: 'moderation-articles',
        navParams: { spot_id: spotId }
      });
    }

    secondaryMenuItems.push(
      {
        label: 'WORDS',
        routeName: 'moderation-restricted-words',
        navParams: { spot_id: spotId }
      },
      { label: 'USERS', routeName: 'moderation-users', navParams: { spot_id: spotId } },
      { label: 'ADVANCED', routeName: 'moderation-advanced', navParams: { spot_id: spotId } }
    );
    const containerClass = classnames(className, 'moderation-container');
    return (
      <div className={containerClass}>
        <SecondaryNavigation title="Moderation" selected={page} menuItems={secondaryMenuItems} />
        <TenantConfigLoader moderatorConfig={moderationModeratorConfigMap} />
        {component}
      </div>
    );
  }
}

const ConnectedModeration = connect(mapStateToProps, mapDispatchToProps)(Moderation);

export default ConnectedModeration;
